import type { components, PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import type { Tour as TourAPI } from '@luxuryescapes/contract-svc-tour'
import { mapObject } from 'lib/object/objectUtils'
import {
  BRAND_SIGNATURE_SERIES,
  END_SPEND_EARN_PROMO_TAG_DATE,
  EXCLUDE_OFFER_PROMO_SPEND_EARN,
  getTourPromoCodeUrgencyMessage,
  TOUR_ASIA_CAMPAIGN,
  TOUR_V2_LE_SOURCE,
  TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD,
  TOUR_V2_PACKAGE_OPTION_BASE,
} from 'constants/tours'
import { arrayToObject, groupBy, nonNullable, sortBy, unique } from 'lib/array/arrayUtils'
import { getPackageOptionType } from 'lib/tours/tourUtils'
import { addMinuteToTimeStr, convertTwelveHourTimeToTimeStr, formatMinutesToHoursMinutes } from 'lib/datetime/time'
import { CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE } from 'constants/checkout'
import uuidV4 from 'lib/string/uuidV4Utils'
import config from 'constants/config'
import { dateDifference, addDays, startOfDay } from 'lib/datetime/dateUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { getTourV2PlacesVisited } from 'lib/tours/extractTourV2VariationDetails'
import { getCurrentIntl } from 'i18n/util/currentIntl'

const NO_PROMO_CODE_SS_TOUR_IDS = ['tour-15275af7-48cb-4a5e-ad0c-eb9b3efc1619', 'tour-e25217eb-4e44-4980-8ad0-508432e4968f']

const EXCLUDE_OPERATOR_PROMO_CODE = ['natgeo', 'gadventures']

type ServerTourOfferV2 = components['schemas']['v2TourOffer']
type ServerTourOfferV2Summary = components['schemas']['v2TourOfferSummary']

// pick only the date portion of the date string (no time + timezone)
export function extractAbsoluteDate(dateString: string) {
  return dateString.substring(0, 10)
}

export function mapTourV2OfferType(
  source: string,
  brand: string,
): App.Tours.V2OfferType {
  if (source === TOUR_V2_LE_SOURCE) {
    if (brand === 'luxuryescapes' || brand === BRAND_SIGNATURE_SERIES) {
      return 'direct_tour'
    }
    return 'partner_tour'
  } else {
    return 'connection_tour'
  }
}

export function mapTourV2ProductType(
  offer: ServerTourOfferV2 | ServerTourOfferV2Summary,
): App.Tours.V2ProductType {
  switch (offer.tourStyle) {
    case 'Deluxe':
      return 'deluxe_tour'
    case 'Premium':
      return 'premium_tour'
    case 'Ultra Lux':
      return 'ultra_lux_tour'
    case 'Signature Series':
      return 'signature_series_tour'
    case 'Insider Series':
      return 'insider_series_tour'
    case 'Custom':
      return 'custom_tour'
    default:
      return offer.productType
  }
}

function mapUrgencyTags(
  urgencyTags: Array<string>,
  showPromoCode: boolean,
  tourOffer?: ServerTourOfferV2 | ServerTourOfferV2Summary,
): Array<App.OfferUrgencyLabel> {
  // While this does nothing now, there are times we need to manaully add urgency tags to the
  // offer - such as the Best of the Decade 2023 campaign. Keep this method around to support
  // future extra mappings
  const tags: Array<App.OfferUrgencyLabel> = []
  const endPromoTagDate = new Date('2025-02-01')
  const { id, brand, campaignTags } = tourOffer ?? {}
  if (showPromoCode &&
    !NO_PROMO_CODE_SS_TOUR_IDS.includes(id ?? '') &&
    !EXCLUDE_OPERATOR_PROMO_CODE.includes(brand ?? '') &&
    new Date() < endPromoTagDate) {
    tags.push({
      type: 'promo_code',
      message: getTourPromoCodeUrgencyMessage(),
    })
  }

  if (campaignTags?.includes(TOUR_ASIA_CAMPAIGN)) {
    tags.push({
      type: 'takeover_promo_code',
    })
  }

  if (tourOffer?.source === TOUR_V2_LE_SOURCE && !EXCLUDE_OFFER_PROMO_SPEND_EARN.includes(id ?? '') && new Date() < END_SPEND_EARN_PROMO_TAG_DATE) {
    tags.push({ type: 'promo_spend_earn' })
  }
  /**
   * Reducing the number of urgency tags shown in customer portal.
   * All back end tags are ignored except for the `ends in` one, which comes out as a "left" urgency tag
   */
  const leftTag = urgencyTags?.find(tag => tag.endsWith('ends in'))
  if (leftTag) {
    tags.push({
      type: 'left',
      message: leftTag,
    })
  }

  return tags
}

function mapTourV2Schedule(schedule: ServerTourOfferV2['schedule']): App.Tours.TourOfferSchedule | undefined {
  if (!schedule) {
    return undefined
  }

  return {
    earlyAccess: schedule.earlyAccess,
    start: schedule.start,
    end: schedule.end,
  }
}

const baseOfferCapacity: App.RoomCapacity = { adults: 4, children: 3, infants: 3, teenagers: 0, total: 4 }
const privateOfferCapacity: App.RoomCapacity = { adults: 3, children: 2, infants: 2, teenagers: 0, total: 3 }
const leOfferCapacity: App.RoomCapacity = { adults: 2, children: 0, infants: 0, teenagers: 0, total: 2 }

function getOfferRoomCapacity(serverOffer: ServerTourOfferV2 | ServerTourOfferV2Summary): Array<App.RoomCapacity> {
  const privateRequestKey = extractTourV2DeparturePrivateRequestKey(serverOffer.departures) ?? undefined
  if (privateRequestKey) {
    return [privateOfferCapacity]
  } else if (serverOffer.source === TOUR_V2_LE_SOURCE) {
    return [leOfferCapacity]
  }
  return [baseOfferCapacity]
}

function mapTourPaymentSchedule(
  serverPaymentSchedule: Utils.ArrayElement<NonNullable<ServerTourOfferV2['paymentScheduleTemplate']>['paymentSchedules']>,
): App.Tours.TourPaymentSchedule | undefined {
  switch (serverPaymentSchedule.amountType) {
    case 'percentage':
      return {
        amountType: 'percentage',
        amountValue: serverPaymentSchedule.amountValue / 100,
      }
    case 'fixed_amount':
      return {
        amountType: 'fixed_amount',
        amountValue: serverPaymentSchedule.amountValue,
      }
  }
}

function mapTourPaymentScheduleTemplate(
  serverPaymentScheduleTemplate: NonNullable<ServerTourOfferV2['paymentScheduleTemplate']>,
): App.Tours.TourPaymentScheduleTemplate {
  const mapped: App.Tours.TourPaymentScheduleTemplate = {
    id: serverPaymentScheduleTemplate.id,
    leadTimeRange: serverPaymentScheduleTemplate.leadTimeRange
      ? {
          minDays: serverPaymentScheduleTemplate.leadTimeRange.minDays,
          maxDays: serverPaymentScheduleTemplate.leadTimeRange.maxDays,
        }
      : undefined,
    minimumLeadDays: serverPaymentScheduleTemplate.minimumLeadDays,
    schedules: nonNullable(serverPaymentScheduleTemplate.paymentSchedules.map(mapTourPaymentSchedule)),
  }

  return mapped
}

export function tourV2OfferMap(
  offer: ServerTourOfferV2,
): App.Tours.TourV2Offer {
  const privateRequestKey = extractTourV2DeparturePrivateRequestKey(offer.departures) ?? undefined
  const optionsByDepartureId = groupBy(offer.options, option => option.fkDepartureId)
  const paymentScheduleTemplate = offer.paymentScheduleTemplate
    ? mapTourPaymentScheduleTemplate(offer.paymentScheduleTemplate)
    : undefined
  const paymentScheduleTemplates = offer.paymentScheduleTemplates?.map(mapTourPaymentScheduleTemplate)

  const departures = mapObject(offer.departures, (departure, departureId) => tourV2DepartureMap(
    departure,
    departureId as string,
    offer.tourOptions[departure.fkTourOptionId],
    optionsByDepartureId.get(departureId as string) ?? [],
  ))

  const mappedOffer: App.Tours.TourV2Offer = {
    analyticsType: 'tourV2',
    productType: mapTourV2ProductType(offer),
    brand: tourV2BrandMap(offer.brandObject),
    departures,
    finePrint: tourV2FinePrintMap(offer.finePrint),
    id: offer.id,
    name: offer.name,
    parentType: 'tour_v2',
    purchasableOptions: offer.options.map(option => tourV2PurchasableOptionMap(option, offer.tourOptions[option.fkTourOptionId])),
    roomTypePricing: mapObject(offer.roomTypePricing, tourV2RoomTypePriceMap),
    purchasableExperienceOptions: arrayToObject(
      nonNullable(offer.optionalExperiences.map(experience => {
        return tourV2PurchasableExperienceOptionMap(experience, offer.id)
      })),
      experience => experience.fkExperienceId!,
    ),
    saleUnit: 'room',
    slug: offer.slug,
    type: mapTourV2OfferType(offer.source, offer.brand),
    variations: mapTourV2OfferVariations(offer.tourOptions, departures, offer.hostsDefaultImage ?? undefined),
    urgencyTags: mapUrgencyTags(offer.urgencyTags, offer.source === TOUR_V2_LE_SOURCE, offer),
    showPricePerDay: offer.showPricePerDay,
    highlightPackages: offer.highlightPackages ?? false,
    activityLevel: offer.activityLevel ?? undefined,
    privateRequestKey,
    source: offer.source,
    recommendedHotelAddOns: offer.recommendedHotelAddOns ?? undefined,
    video: offer.video ?? undefined,
    sponsors: offer.sponsors ?? [],
    hosts: offer.hosts ?? [],
    depositThresholds: {
      numberOfDays: offer.depositNumberOfDueDays ?? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD,
      percentage: tourV2DepositAmountMap(offer.depositAmount ?? 0, offer.depositType),
    },
    operatedBy: offer.operatedBy,
    guideType: offer.guideType ?? undefined,
    luxPlus: {
      access: offer.luxPlus.access,
      hasMemberPrices: offer.options.some(option => !!option.memberPrice),
    },
    isAgentHubExclusive: config.agentHub.isEnabled && Boolean(offer.isAgentHubExclusive),
    schedule: mapTourV2Schedule(offer.schedule),
    roomCapacity: getOfferRoomCapacity(offer),
    paymentScheduleInfo: ((offer.paymentScheduleTemplateId || offer.paymentScheduleTemplateIds?.length) && offer.supplierDueDays !== undefined) ? {
      staticSchedule: {
        templateId: offer.paymentScheduleTemplateId ?? offer.paymentScheduleTemplateIds?.[0] ?? '',
        templateIds: offer.paymentScheduleTemplateIds,
        supplierPaymentNumberOfDays: offer.supplierDueDays,
      },
    } : undefined,
    isSignatureSeries: BRAND_SIGNATURE_SERIES === offer.brand,
    paymentScheduleTemplate: paymentScheduleTemplate ?? paymentScheduleTemplates?.[0],
    paymentScheduleTemplates,
    privateJourney: offer.privateJourney,
    campaignTags: offer.campaignTags,
    nextYearTour: offer.nextYearTour ?? null,
    previousYearTour: offer.previousYearTour ?? null,
    similarTours: offer.similarTours ?? [],
    optionalExperienceBookingDaysBeforeDeparture: offer.optionalExperienceBookingDaysBeforeDeparture ?? undefined,
  }

  if (offer.reviewsRating) {
    mappedOffer.rating = {
      score: offer.reviewsRating,
      reviewsTotal: offer.reviewsTotal ?? 0,
      reviewsSource: offer.reviewsSource ?? 'luxuryescapes',
    }
  }

  if (typeof offer.depositAmount !== 'undefined' && typeof offer.depositType !== 'undefined') {
    mappedOffer.depositAmount = tourV2DepositAmountMap(offer.depositAmount ?? 0, offer.depositType)
    mappedOffer.depositType = offer.depositType
  }

  if (offer.faq) {
    mappedOffer.faq = offer.faq.map(tourV2FAQMap)
  }

  return mappedOffer
}

export function tourV2OfferSummaryMap(
  offer: ServerTourOfferV2Summary,
): App.Tours.TourV2OfferSummary {
  const optionsByDepartureId = groupBy(offer.options, option => option.fkDepartureId)
  const paymentScheduleTemplate = offer.paymentScheduleTemplate
    ? mapTourPaymentScheduleTemplate(offer.paymentScheduleTemplate)
    : undefined
  const paymentScheduleTemplates = offer.paymentScheduleTemplates?.map(mapTourPaymentScheduleTemplate)
  const departures = mapObject(offer.departures, (departure, departureId) => tourV2DepartureMap(
    departure,
    departureId as string,
    offer.tourOptions[departure.fkTourOptionId],
    optionsByDepartureId.get(departureId as string) ?? [],
  ))

  const mapped: App.Tours.TourV2OfferSummary = {
    analyticsType: 'tourV2',
    productType: mapTourV2ProductType(offer),
    allVariationsCount: offer.tourOptionCount,
    brand: tourV2BrandMap(offer.brandObject),
    departures,
    id: offer.id,
    summaryId: `${offer.id}__${Object.keys(offer.tourOptions)[0]}`,
    name: offer.name,
    parentType: 'tour_v2',
    purchasableOptions: offer.options.map(option => tourV2PurchasableOptionMap(option, offer.tourOptions[option.fkTourOptionId])),
    roomTypePricing: mapObject(offer.roomTypePricing, tourV2RoomTypePriceMap),
    purchasableExperienceOptions: arrayToObject(
      nonNullable(offer.optionalExperiences.map(experience => {
        return tourV2PurchasableExperienceOptionMap(experience, offer.id)
      })),
      experience => experience.fkExperienceId!,
    ),
    saleUnit: 'room',
    slug: offer.slug,
    type: mapTourV2OfferType(offer.source, offer.brand),
    variations: mapTourV2OfferVariations(offer.tourOptions, departures),
    urgencyTags: mapUrgencyTags(offer.urgencyTags, offer.source === TOUR_V2_LE_SOURCE, offer),
    showPricePerDay: offer.showPricePerDay,
    highlightPackages: offer.highlightPackages ?? false,
    depositType: offer.depositType,
    depositAmount: tourV2DepositAmountMap(offer.depositAmount ?? 0, offer.depositType),
    source: offer.source as App.Tours.V2TourSource,
    recommendedHotelAddOns: offer.recommendedHotelAddOns ?? undefined,
    sponsors: offer.sponsors ?? [],
    hosts: offer.hosts ?? [],
    depositThresholds: {
      numberOfDays: offer.depositNumberOfDueDays ?? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD,
      percentage: tourV2DepositAmountMap(offer.depositAmount ?? 0, offer.depositType),
    },
    operatedBy: offer.operatedBy,
    guideType: offer.guideType ?? undefined,
    luxPlus: {
      access: offer.luxPlus.access,
      hasMemberPrices: offer.options.some(option => !!option.memberPrice),
    },
    isAgentHubExclusive: config.agentHub.isEnabled && Boolean(offer.isAgentHubExclusive),
    schedule: mapTourV2Schedule(offer.schedule),
    roomCapacity: getOfferRoomCapacity(offer),
    isSignatureSeries: BRAND_SIGNATURE_SERIES === offer.brand,
    paymentScheduleInfo: ((offer.paymentScheduleTemplateId || offer.paymentScheduleTemplateIds?.length) && offer.supplierDueDays !== undefined) ? {
      staticSchedule: {
        templateId: offer.paymentScheduleTemplateId ?? offer.paymentScheduleTemplateIds?.[0] ?? '',
        templateIds: offer.paymentScheduleTemplateIds,
        supplierPaymentNumberOfDays: offer.supplierDueDays,
      },
    } : undefined,
    paymentScheduleTemplate: paymentScheduleTemplate ?? paymentScheduleTemplates?.[0],
    paymentScheduleTemplates,
    privateJourney: offer.privateJourney,
    campaignTags: offer.campaignTags,
    nextYearTour: offer.nextYearTour ?? null,
    previousYearTour: offer.previousYearTour ?? null,
    similarTours: offer.similarTours ?? [],
  }

  if (offer.reviewsRating) {
    mapped.rating = {
      score: offer.reviewsRating,
      reviewsTotal: offer.reviewsTotal,
      reviewsSource: offer.reviewsSource ?? 'luxuryescapes',
    }
  }

  return mapped
}

export function tourV2BrandMap(
  brandObject: PublicOfferV2.Brand,
): App.Tours.TourV2OfferBrand {
  const {
    code,
    name,
    svcLogoId,
  } = brandObject

  const brand: App.Tours.TourV2OfferBrand = {
    code,
    name,
  }

  if (svcLogoId) {
    brand.logoImage = { id: svcLogoId, title: `${name} logo` }
  }

  return brand
}

export function tourV2DepartureMap(
  departure: PublicOfferV2.Departure,
  departureId: string,
  tourOption: PublicOfferV2.TourOption,
  options: Array<PublicOfferV2.PurchasableOption>,
): App.Tours.TourV2OfferDeparture {
  const startDate = extractAbsoluteDate(departure.startDate)

  const mappedDeparture: App.Tours.TourV2OfferDeparture = {
    endDate: extractAbsoluteDate(departure.endDate),
    fkSeasonId: departure.fkSeasonId,
    fkVariationId: getTourV2VariationId(tourOption),
    id: departureId,
    isGuaranteed: departure.definiteDeparture,
    startDate,
    month: new Date(startDate).getMonth(),
    year: new Date(startDate).getFullYear(),
    status: departure.availability.status,
    urgencyTags: mapUrgencyTags(departure.urgencyTags, false),
    privateRequestKey: departure.privateRequestKey,
    duration: dateDifference(new Date(departure.endDate), new Date(departure.startDate)).days,
    options: departure.availability.status === 'available' ? options.map(option => tourV2PurchasableOptionMap(option, tourOption)) : [],
    currencyCode: departure.currencyCode ?? undefined,
    startTimeLocal: departure.startTimeLocal ?? undefined,
    endTimeLocal: departure.endTimeLocal ?? undefined,
    groupSize: departure.groupSize,
    // below will be changed shortly to get the handback date from PO and svc-tour
    handbackDate: departure.handBackDate ?? addDays(new Date(departure.startDate), -60).toDateString(),
    itineraryChanges: departure.itineraryChanges ?? undefined,
  }
  return mappedDeparture
}

function tourV2FinePrintSectionMap(
  finePrintItem: TourAPI.FinePrintItem,
): App.Tours.V2OfferFinePrintSection {
  return {
    category: finePrintItem.category,
    title: finePrintItem.title,
    body: finePrintItem.body,
  }
}

export function tourV2FinePrintMap(
  finePrint: TourAPI.FinePrint,
): App.Tours.TourV2OfferFinePrint {
  return {
    sections: finePrint.items?.map(tourV2FinePrintSectionMap) ?? [],
  }
}

export function tourV2PurchasableOptionMap(
  purchasableOption: PublicOfferV2.PurchasableOption,
  tourOption: PublicOfferV2.TourOption,
): App.Tours.TourV2OfferPurchasableOption {
  const mappedPurchasableOption: App.Tours.TourV2OfferPurchasableOption = {
    fkDepartureId: purchasableOption.fkDepartureId,
    fkRoomTypePricingId: purchasableOption.fkRoomTypePricingId,
    fkSeasonId: purchasableOption.fkSeasonId,
    fkTourId: purchasableOption.fkTourId,
    fkVariationId: getTourV2VariationId(tourOption),
    fullPrice: purchasableOption.fullPrice,
    memberPrice: Number(purchasableOption.memberPrice),
    price: purchasableOption.price,
    roomType: purchasableOption.roomType as App.Tours.V2OfferRoomType, // TODO: this could be a type mapper. needs fallback
    packageType: getPackageOptionType(purchasableOption.roomType as App.Tours.V2OfferRoomType),
    // a nullable inventory left is the equiv of unlimited
    inventoryLeft: purchasableOption.inventoryLeft ?? 999,
  }

  if (purchasableOption.valuedAtPrice) {
    mappedPurchasableOption.valuedAt = purchasableOption.valuedAtPrice
  }

  return mappedPurchasableOption
}

export function tourV2PurchasableExperienceOptionMap(
  purchasableExperienceOption: components['schemas']['v2TourOptionalExperience'],
  tourId: string,
): Partial<App.Tours.TourV2OfferPurchasableOption> | null {
  if (purchasableExperienceOption.price === null) return null
  return {
    fkExperienceId: purchasableExperienceOption.id,
    fkTourId: tourId,
    fullPrice: purchasableExperienceOption.price,
    price: purchasableExperienceOption.price,
    timeSlots: tourV2TourExperienceTimeSlotMap(purchasableExperienceOption.timeSlots, purchasableExperienceOption.duration),
    dayNumbers: purchasableExperienceOption.dayNumbers,
    duration: purchasableExperienceOption.duration ?? undefined,
    name: purchasableExperienceOption.name,
    description: purchasableExperienceOption.description ?? undefined,
    nonRefundable: purchasableExperienceOption.nonRefundable,
  }
}

export function tourV2RoomTypePriceMap(
  roomTypePrice: PublicOfferV2.RoomTypePrice,
): App.Tours.TourV2OfferRoomTypePrice {
  const mappedRoomTypePrice: App.Tours.TourV2OfferRoomTypePrice = {
    id: roomTypePrice.id,
    maxChildDiscounts: roomTypePrice.maxChildDiscounts || 0,
  }

  if (roomTypePrice.childPrice && roomTypePrice.childPrice >= 0) {
    mappedRoomTypePrice.childPrice = roomTypePrice.childPrice
  }

  return mappedRoomTypePrice
}

export function tourV2InclusionMap(
  inclusion: TourAPI.Inclusion,
): App.Tours.V2OfferInclusion {
  return {
    title: inclusion.title,
    body: inclusion.body,
  }
}

export function tourV2ImageMap(
  image: PublicOfferV2.Image,
): App.Image {
  return {
    id: image.id,
    title: image.title,
  }
}

export function tourV2TourExperienceTimeSlotMap(timeSlots?: Array<string>, duration?: number | null) {
  if (!duration) return []
  return timeSlots?.map((timeSlot) => {
    const startTime = convertTwelveHourTimeToTimeStr(timeSlot)
    const endTime = addMinuteToTimeStr(startTime, duration)
    return `${startTime} - ${endTime}`
  }) ?? []
}

export function tourV2ExperienceMap(
  tourExperience: components['schemas']['v2TourOptionalExperience'],
  itineraryItem: PublicOfferV2.ItineraryItem,
): App.Tours.TourExperience | null {
  const location = itineraryItem?.locationsVisitedDetails?.map((location) => location.name).join(' - ') ?? itineraryItem?.locationsVisited.join(' - ') ?? ''
  if (tourExperience.price === null) return null
  return {
    id: tourExperience.id,
    itineraryId: itineraryItem.id,
    dayNumbers: tourExperience.dayNumbers,
    name: tourExperience.name,
    description: tourExperience.description,
    timeSlots: tourV2TourExperienceTimeSlotMap(tourExperience.timeSlots, tourExperience.duration),
    price: tourExperience.price,
    location,
    duration: tourExperience.duration ?? undefined,
    durationFormatted: tourExperience.duration ? formatMinutesToHoursMinutes(tourExperience.duration, getCurrentIntl()) : null,
    nonRefundable: tourExperience.nonRefundable,
  }
}

export function tourV2ItineraryItemMap(
  itineraryItem: PublicOfferV2.ItineraryItem,
  hostsDefaultImage?: string,
): App.Tours.TourV2OfferItineraryItem {
  const mappedItineraryItem: App.Tours.TourV2OfferItineraryItem = {
    id: itineraryItem.id,
    description: itineraryItem.description,
    duration: itineraryItem.duration,
    locationsVisited: itineraryItem.locationsVisited ?? [],
    meals: itineraryItem.meals ?? [],
    startDay: itineraryItem.startDay,
    title: itineraryItem.title,
    locationsVisitedDetails: itineraryItem.locationsVisitedDetails ?? [],
    specialsEvents: itineraryItem.specialCallOuts.map(special => ({
      hostImageId: special.hostImageId === 'default' ? hostsDefaultImage : special.hostImageId ?? undefined,
      description: special.description,
      isFlash: special.isFlash,
    })) ?? [],
    experiences: itineraryItem.optionalExperiences
      .map(experience => tourV2ExperienceMap(experience, itineraryItem))
      .filter(Boolean) as Array<App.Tours.TourExperience> ?? [],
    transports: itineraryItem.transports ?? [],
  }

  if (itineraryItem.accommodation) {
    mappedItineraryItem.accommodation = itineraryItem.accommodation
  }
  if (itineraryItem.region) {
    mappedItineraryItem.region = itineraryItem.region
  }

  return mappedItineraryItem
}

export function tourV2ReservationSnapshotItineraryItemMap(
  itineraryItem: TourAPI.SnapshotItineraryItem,
): App.Tours.TourV2OfferItineraryItem {
  const mappedItineraryItem: App.Tours.TourV2OfferItineraryItem = {
    description: itineraryItem.description,
    duration: itineraryItem.duration,
    locationsVisited: itineraryItem.locationsVisited ?? [],
    meals: itineraryItem.meals ?? [],
    startDay: itineraryItem.startDay,
    title: itineraryItem.title,
    locationsVisitedDetails: [],
    specialsEvents: [],
    experiences: [],
    transports: [],
  }

  if (itineraryItem.accommodation) {
    mappedItineraryItem.accommodation = itineraryItem.accommodation
  }
  if (itineraryItem.region) {
    mappedItineraryItem.region = itineraryItem.region
  }

  return mappedItineraryItem
}

function getTourV2VariationId(tourOption: PublicOfferV2.TourOption) {
  return tourOption.slug ?? tourOption.id
}

export function mapTourV2OfferVariations(
  tourOptions: { [key: string]: components['schemas']['v2TourOption'] },
  departures: Record<string, App.Tours.TourV2OfferDeparture>,
  hostsDefaultImage?: string,
): App.Tours.TourV2Offer['variations'] {
  const departuresByVariationId = groupBy(departures, dep => dep.fkVariationId)
  const tourOptionsAsArray = Object.values(tourOptions)
  const baseTourOption = tourOptionsAsArray.find((tourOption) => tourOption.sourceTourOptionName === TOUR_V2_PACKAGE_OPTION_BASE)

  const variationsWithDepartures = tourOptionsAsArray.filter(option => !!departuresByVariationId.get(getTourV2VariationId(option)))
  return arrayToObject(
    variationsWithDepartures,
    tourOption => getTourV2VariationId(tourOption),
    tourOption => {
      const baseTourOptionToCompare = tourOption.sourceTourOptionName !== TOUR_V2_PACKAGE_OPTION_BASE ? baseTourOption : undefined
      const departures = departuresByVariationId.get(getTourV2VariationId(tourOption)) ?? []
      return tourV2VariationMap(
        tourOption,
        departures,
        baseTourOptionToCompare,
        hostsDefaultImage,
      )
    },
  )
}

const sevenDaysInFuture = addDays(startOfDay(new Date()), 7)
function tourV2VariationMap(
  tourOption: PublicOfferV2.TourOption,
  departures: Array<App.Tours.TourV2OfferDeparture>,
  baseTourOption?: PublicOfferV2.TourOption,
  hostsDefaultImage?: string,
): App.Tours.TourV2OfferVariation {
  const itineraryDestinationsDiff = (baseTourOption) ?
      itineraryDiffDestinationsVisited(tourOption.defaultSeason.itinerary, baseTourOption.defaultSeason.itinerary) :
      extractItinerariesLocations(tourOption.defaultSeason.itinerary)

  const variationId = getTourV2VariationId(tourOption)
  const itinerary = sortBy(
    tourOption.defaultSeason.itinerary?.map(itinerary => tourV2ItineraryItemMap(itinerary, hostsDefaultImage)),
    itinerary => itinerary.startDay, 'asc',
  ) ?? []

  const mappedVariation: App.Tours.TourV2OfferVariation = {
    countriesVisited: tourOption.defaultSeason.countriesVisited ?? [],
    placesVisited: getTourV2PlacesVisited(itinerary),
    description: tourOption.defaultSeason.copy.description,
    diningInclusions: tourOption.defaultSeason.copy.diningInclusions?.map(tourV2InclusionMap) ?? [],
    endLocation: tourOption.defaultSeason.endLocation ?? undefined,
    endLocationCountryCode: tourOption.defaultSeason.endLocationDetails?.countryCode ?? undefined,
    endLocationPlaceId: tourOption.defaultSeason.endLocationDetails?.placeId ?? undefined,
    fkSeasonId: tourOption.defaultSeason.id,
    fkTourOptionId: tourOption.id,
    id: variationId,
    images: tourOption.defaultSeason.images.map(tourV2ImageMap) ?? [],
    itinerary,
    name: tourOption.defaultSeason.name,
    startLocation: tourOption.defaultSeason.startLocation ?? undefined,
    startLocationPlaceId: tourOption.defaultSeason.startLocationDetails?.placeId ?? undefined,
    startLocationCountryCode: tourOption.defaultSeason.startLocationDetails?.countryCode ?? undefined,
    travelInclusions: tourOption.defaultSeason.copy.travelInclusions?.map(tourV2InclusionMap) ?? [],
    canRequestPrivateDepartures: !!tourOption.isPrivateRequest,
    isReverseItinerary: !!tourOption.isReverseItinerary,
    privateRequestFromPrice: tourOption.privateRequestFromPrice ?? undefined,
    maxPax: tourOption.maxPax ?? undefined,
    maxAge: tourOption.maxAge ?? undefined,
    inclusionItems: tourOption.defaultSeason.inclusionItems.map<App.OfferInclusion>(item => ({
      id: uuidV4(),
      description: item.inclusionBody,
      symbol: determineOfferFeatureSymbol(item.categoryIcon?.split(',')[0]),
      isHighlighted: item.isHighlight,
      isFlash: item.isFlash,
      isLuxPlusBonus: !!item.isLuxPlusBonus,
    })),
    packageOptionName: tourOption.sourceTourOptionName ?? undefined,
    itineraryDestinations: extractItinerariesLocations(tourOption.defaultSeason.itinerary),
    itineraryDestinationsDiff,
    availablePackageUpgrades: tourOption.availablePackageUpgrades?.map<App.Tours.PackageUpgrade>((packageUpgrade) => ({
      packageType: packageUpgrade.packageOption.toLowerCase(),
      packageOption: packageUpgrade.packageOption,
      packageOptionDisplayName: packageUpgrade.packageOptionDisplayName,
      inclusions: packageUpgrade.inclusions.map<App.OfferInclusion>(item => ({
        id: uuidV4(),
        description: item.inclusionBody,
        symbol: determineOfferFeatureSymbol(item.categoryIcon?.split(',')[0]),
        isHighlighted: item.isHighlight,
      })),
    })) ?? [],
    baseTourOptionId: baseTourOption ? getTourV2VariationId(baseTourOption) : undefined,
    notes: tourOption.defaultSeason.notes ?? [],
    departures: departures.filter(dep => new Date(dep.startDate) > sevenDaysInFuture && dep.status !== 'hidden'),
  }

  if (tourOption.defaultSeason.maxChildPriceAge) {
    mappedVariation.maxChildPriceAge = tourOption.defaultSeason.maxChildPriceAge
  }
  if (tourOption.defaultSeason.minChildPriceAge) {
    mappedVariation.minChildPriceAge = tourOption.defaultSeason.minChildPriceAge
  }
  if (tourOption.defaultSeason.routeMapImage) {
    mappedVariation.routeMapImage = { id: tourOption.defaultSeason.routeMapImage, title: `${tourOption.defaultSeason.name} route map` }
  }

  return mappedVariation
}

function itineraryDiffDestinationsVisited(currentDestinations: Array<PublicOfferV2.ItineraryItem>, baseDestinations: Array<PublicOfferV2.ItineraryItem>) {
  const itinerariesA = extractItinerariesLocations(currentDestinations)
  const itinerariesB = extractItinerariesLocations(baseDestinations)
  return itinerariesA.filter(location => !itinerariesB.includes(location))
}

function extractItinerariesLocations(itineraryItems: Array<PublicOfferV2.ItineraryItem>) {
  return unique(
    itineraryItems
      .flatMap(itinerary => itinerary.locationsVisitedDetails)
      .map(location => location.name),
  )
}

export function tourV2FAQMap(
  faq: TourAPI.FAQQuestion,
): App.Tours.TourV2OfferFrequentlyAskedQuestion {
  return {
    content: faq.content,
    key: faq.key,
    title: faq.title,
  }
}

export function tourV2DepositAmountMap(depositAmount: number, depositType?: App.Tours.V2OfferDepositType) {
  if (depositType === 'percentage') {
    return depositAmount * 100
  }

  if (depositType === 'default') {
    return 0
  }

  return depositAmount
}

export function extractTourV2DeparturePrivateRequestKey(departures: { [key: string]: components['schemas']['v2TourDeparture'] }): string | null {
  const departuresPrivateKeys = Object.values(departures)
    .filter(departure => !!departure.privateRequestKey)
    .map(departure => departure.privateRequestKey)
  return departuresPrivateKeys?.[0] ?? null
}

export function tourV2ExperienceSnapshotMap(
  tourExperienceSnapshot: TourAPI.SnapshotOptionalExperience,
): App.Tours.TourV2OrderExperienceItem {
  return {
    id: tourExperienceSnapshot.id,
    name: tourExperienceSnapshot.name,
    description: tourExperienceSnapshot.description ?? undefined,
    itineraryId: tourExperienceSnapshot.itineraryId,
    dayNumber: tourExperienceSnapshot.dayNumber,
    duration: tourExperienceSnapshot.duration ?? undefined,
    price: tourExperienceSnapshot.price,
    total: tourExperienceSnapshot.price * tourExperienceSnapshot.count,
    timeSlot: tourExperienceSnapshot.timeSlot ?? undefined,
    count: tourExperienceSnapshot.count,
    date: tourExperienceSnapshot.date,
    location: tourExperienceSnapshot.location,
    status: tourExperienceSnapshot.status,
    itemType: 'tour_experience',
  }
}

export function tourV2ExperienceWithExistingOrder(
  orderTourExperienceItem: App.Tours.TourV2OrderExperienceItem,
  purchasableTourOptionalExperience: App.Tours.TourV2OfferPurchasableOption,
): App.Checkout.TourV2ExperienceItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    offerId: purchasableTourOptionalExperience.fkTourId,
    date: orderTourExperienceItem.date,
    itemType: CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE,
    occupants: [{ adults: orderTourExperienceItem.count }],
    purchasableOption: {
      ...purchasableTourOptionalExperience,
      timeSlot: orderTourExperienceItem.timeSlot,
      date: orderTourExperienceItem.date,
      day: orderTourExperienceItem.dayNumber,
      duration: orderTourExperienceItem.duration,
    },
  }
}
